/**
 * Overview tab: pre-match (odds, match info, table, form) vs live/finished
 * (events → stats → full-time note → match info → table positions; no “Match info”-style titles).
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { SectionCard } from '../SectionCard';
import { MatchHubMatchInfoCard } from './MatchHubMatchInfoCard';
import { MatchHubCurrentTablePositionsCard } from './MatchHubCurrentTablePositionsCard';
import { MatchHubLiveStatsBlock } from './MatchHubLiveStatsBlock';
import { MatchHubEventsTimeline } from './MatchHubEventsTimeline';
import { MatchHubTeamFactsCard } from '../MatchHubTeamFacts/MatchHubTeamFactsCard';
import { MatchHubTeamFactNotesCard } from '../MatchHubTeamFacts/MatchHubTeamFactNotesCard';
import { useTheme } from '../../../src/theme/useTheme';
import type { MatchHubMockData } from '../../../src/match/types/matchHub.types';
import { getMatchHubOverviewStyles } from './MatchHubOverviewPanel.styles';

export interface MatchHubOverviewPanelProps {
  data: MatchHubMockData;
  onOpenTableTab?: () => void;
}

function formPillStyles(
  styles: ReturnType<typeof getMatchHubOverviewStyles>,
  r: 'W' | 'D' | 'L',
) {
  if (r === 'W') return [styles.formPill, styles.formPillWin];
  if (r === 'L') return [styles.formPill, styles.formPillLoss];
  return [styles.formPill, styles.formPillDraw];
}

export const MatchHubOverviewPanel: React.FC<MatchHubOverviewPanelProps> = ({ data, onOpenTableTab }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubOverviewStyles(theme), [theme]);

  if (data.phase === 'pre') {
    return (
      <View testID="match-hub-overview-pre">
        <SectionCard title="Pre-Match Odds (1X2)">
          <View style={styles.oddsRow}>
            <View style={styles.oddsCell}>
              <Text style={styles.oddsLabel}>{data.home.shortName}</Text>
              <Text style={styles.oddsValue}>{data.odds1x2.home.toFixed(2)}</Text>
            </View>
            <View style={styles.oddsCell}>
              <Text style={styles.oddsLabel}>Draw</Text>
              <Text style={styles.oddsValue}>{data.odds1x2.draw.toFixed(2)}</Text>
            </View>
            <View style={styles.oddsCell}>
              <Text style={styles.oddsLabel}>{data.away.shortName}</Text>
              <Text style={styles.oddsValue}>{data.odds1x2.away.toFixed(2)}</Text>
            </View>
          </View>
        </SectionCard>
        <MatchHubMatchInfoCard data={data} />
        {data.standings.length > 0 && (
          <MatchHubCurrentTablePositionsCard data={data} onSeeAllPress={onOpenTableTab} />
        )}
        <SectionCard title="Form Guide (Last 5 Games)">
          {data.form.home.length === 0 && data.form.away.length === 0 ? (
            <Text style={styles.formEmptyText}>Recent form isn’t available for these teams yet.</Text>
          ) : (
            <>
              <View style={styles.formRow}>
                <View style={styles.formTeamBlock}>
                  <Text style={styles.formTeamName} numberOfLines={1}>
                    {data.home.shortName}
                  </Text>
                  <View style={styles.formPills}>
                    {data.form.home.length === 0 ? (
                      <Text style={styles.formEmptyText}>—</Text>
                    ) : (
                      data.form.home.map((r, i) => (
                        <View key={`h-${i}`} style={formPillStyles(styles, r)}>
                          <Text style={styles.formPillText}>{r}</Text>
                        </View>
                      ))
                    )}
                  </View>
                </View>
              </View>
              <View style={styles.formRow}>
                <View style={styles.formTeamBlock}>
                  <Text style={styles.formTeamName} numberOfLines={1}>
                    {data.away.shortName}
                  </Text>
                  <View style={styles.formPills}>
                    {data.form.away.length === 0 ? (
                      <Text style={styles.formEmptyText}>—</Text>
                    ) : (
                      data.form.away.map((r, i) => (
                        <View key={`a-${i}`} style={formPillStyles(styles, r)}>
                          <Text style={styles.formPillText}>{r}</Text>
                        </View>
                      ))
                    )}
                  </View>
                </View>
              </View>
            </>
          )}
        </SectionCard>
        <MatchHubTeamFactsCard data={data} />
        <MatchHubTeamFactNotesCard data={data} />
      </View>
    );
  }

  const stats = data.stats;

  return (
    <View testID="match-hub-overview-live-finished">
      <SectionCard testID="match-hub-events-section">
        <MatchHubEventsTimeline events={data.events} />
      </SectionCard>
      {stats !== undefined && (
        <MatchHubLiveStatsBlock home={data.home} away={data.away} stats={stats} />
      )}
      <MatchHubMatchInfoCard data={data} />
      {data.standings.length > 0 && (
        <MatchHubCurrentTablePositionsCard data={data} onSeeAllPress={onOpenTableTab} />
      )}
    </View>
  );
};
